import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// UI Components
import {
  Button,
  Form,
  FormGroup,
  FormText,
  FormFeedback,
  Label,
  Input,
  Row,
  Col,
  Progress,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

// State
import { toastr } from "react-redux-toastr";
import { checkIfUserAvailable, registerUser } from "../../state/actions/userActions";
import useDebounce from "../hooks/useDebounce";

const UserSchema = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
};

const PasswordStrength = ({ password, passwordStrength }) => {
  const { requirements, error } = passwordStrength;
  const colors = ["danger", "warning", "primary", "success"];
  const messages = ["weak", "okay", "strong", "excellent!"];

  if (error) {
    return <FormFeedback>{error}</FormFeedback>;
  }

  return (
    <FormText style={{ visibility: password ? "initial" : "hidden" }}>
      Password Strength
      <Progress color={colors[requirements.length - 1]} value={requirements.length} max={4}>
        {messages[requirements.length - 1]}
      </Progress>
    </FormText>
  );
};

/**
 * The registration form
 * @constructor
 */
const RegisterUser = ({ errors, loading, isAvailable, checkIfUserAvailable, registerUser }) => {
  // Local Variables
  const [newUser, setNewUser] = useState(UserSchema);
  const debouncedEmail = useDebounce(newUser.email, 500); // Prevent checks from pinging while user is still typing

  // Check if email has already been registered
  useEffect(() => {
    // Make sure user has entered something
    if (debouncedEmail) {
      checkIfUserAvailable({ email: debouncedEmail });
    }
  }, [debouncedEmail, checkIfUserAvailable]);

  const setUserProp = ({ target }) => {
    const user = { ...newUser };

    user[target.name] = target.value;

    setNewUser(user);
  };

  const testPasswordStrength = ({ password }) => {
    const requirements = [];

    if (password.length < 6) {
      return { error: "Password must be at least 6 characters", requirements: [] };
    }
    if (password.toUpperCase() !== password) {
      requirements.push("lowercase");
    }
    if (password.toLowerCase() !== password) {
      requirements.push("uppercase");
    }
    if (/\d/.test(password)) {
      requirements.push("number");
    }
    if (/[^\w\d]/.test(password)) {
      requirements.push("special");
    }
    if (requirements.length < 2) {
      return {
        error:
          "Password must contain at least 2 of the following: lowercase, uppercase, number, special character",
        requirements: [],
      };
    }

    return { requirements };
  };

  // Validity checks
  const valid_first_name = /\w{3,}/.test(newUser.first_name);
  const valid_last_name = /\w{3,}/.test(newUser.last_name);
  const valid_email = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(newUser.email);
  const passwordStrength = testPasswordStrength(newUser);

  const submit = () => {
    if (!valid_first_name) {
      return toastr.error("Please check the first name value");
    }
    if (!valid_last_name) {
      return toastr.error("Please check the first name value");
    }
    if (!valid_email) {
      return toastr.error("Please include check the provided email address");
    }
    if (passwordStrength.error) {
      return toastr.error(passwordStrength.error);
    }

    registerUser(newUser);
  };

  return (
    <Form>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>First Name</Label>
            <Input
              bsSize="lg"
              type="text"
              name="first_name"
              placeholder="First name"
              value={newUser.first_name}
              onChange={setUserProp}
              valid={valid_first_name}
              invalid={!!newUser.first_name.length && !valid_first_name}
            />

            <FormFeedback>
              {newUser.first_name && !valid_first_name && "Please enter a valid first name"}
            </FormFeedback>
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Last Name</Label>
            <Input
              bsSize="lg"
              type="text"
              name="last_name"
              placeholder="Last name"
              value={newUser.last_name}
              onChange={setUserProp}
              valid={valid_last_name}
              invalid={!!newUser.last_name.length && !valid_last_name}
            />

            <FormFeedback>
              {newUser.last_name && !valid_last_name && "Please enter a valid last name"}
            </FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Email</Label>
        <Input
          bsSize="lg"
          type="text"
          name="email"
          placeholder="Email address"
          value={newUser.email}
          onChange={setUserProp}
          valid={valid_email && isAvailable === true}
          invalid={(!!newUser.email && !valid_email) || isAvailable === false}
        />

        <FormFeedback valid>
          <strong>{newUser.email}</strong> is valid and available
        </FormFeedback>
        <FormFeedback>
          {valid_email
            ? errors.email || `${newUser.email} is not available`
            : "Invalid email address"}
        </FormFeedback>
      </FormGroup>

      <FormGroup>
        <Label>Password</Label>
        <Input
          bsSize="lg"
          type="password"
          name="password"
          placeholder="Password"
          value={newUser.password}
          onChange={setUserProp}
          valid={newUser.password.length > 3 && !passwordStrength.error}
          invalid={newUser.password.length > 3 && !!passwordStrength.error}
        />

        <PasswordStrength password={newUser.password} passwordStrength={passwordStrength} />
      </FormGroup>

      <div className="text-center mt-3">
        <Button
          type="button"
          color="primary"
          size="lg"
          onClick={submit}
          disabled={loading || !isAvailable || !valid_email || !!passwordStrength.error}
        >
          Register
          {loading && <FontAwesomeIcon icon={faSpinner} className="ml-2" spin />}
        </Button>
      </div>
    </Form>
  );
};

RegisterUser.propTypes = {
  errors: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  isAvailable: PropTypes.bool,
  registerUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  errors: state.user.errors,
  loading: state.user.loading,
  isAvailable: state.user.isAvailable,
});

export default connect(mapStateToProps, { checkIfUserAvailable, registerUser })(RegisterUser);
